import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { Utils } from '../../../Common/Utils';
import { ButtonType } from '../../../views/Button';
import {
    Button,
    Card,
    CardType,
    GhostButton,
    LabelBody,
    LabelBodySmall,
    LabelDisplay1,
    LabelHeader5,
    SecondaryButton,
    Spacer,
    TertiaryButton,
} from '../components';
import { trialInfoStatusAtom } from '../data';
import {
    ArticleIcon,
    MegaphoneIcon,
    NotesIcon,
    TargetCorrectIcon,
    VideoPlayerIcon,
} from '../icons';
import {
    ExerciseDto,
    ExerciseStatus,
    ExerciseType,
    TrialAvailability,
} from '../interfaces';
import { ExerciseStatusView } from './ExerciseStatusView';
import { SmallVideoCardView } from './SmallVideoCardView';
import { VideoCardView } from './VideoCardView';
import { TrialInfoCard } from './TrialInfoCard';

type Props = {
    exercise: ExerciseDto;
    showVideoInline?: boolean;
    onOpenExercise: () => void;
    onVideoPlayed?: () => void;
    class?: string;
    className?: string;
    style?: React.CSSProperties;
    buttonType?: ButtonType;
    children?: React.ReactNode;
};
export function ExerciseView({ exercise, ...props }: Props) {
    const trialInfoStatus = useAtomValue(trialInfoStatusAtom);
    if (
        trialInfoStatus != null &&
        exercise.AvailabilityForTrialUsers ==
            TrialAvailability.AvailableButBlocked
    ) {
        return (
            <div className="relative">
                <div inert="">
                    <InternalExerciseView exercise={exercise} {...props} />
                </div>
                <TrialInfoCard type={trialInfoStatus} />
            </div>
        );
    } else {
        return <InternalExerciseView exercise={exercise} {...props} />;
    }
}
function InternalExerciseView({
    exercise,
    showVideoInline,
    onOpenExercise,
    onVideoPlayed,
    className,
    style,
    buttonType,
    children,
    ...props
}: Props) {
    const hasLoggedData = useMemo(
        () => Object.keys(exercise.Data).length > 0,
        [exercise],
    );

    if (exercise.Type === ExerciseType.Hidden) {
        return null;
    }

    if (exercise.Type === ExerciseType.SmallMedia) {
        return (
            <SmallVideoCardView
                key={exercise.Id}
                className={className ?? props.class}
                exercise={exercise}
                onOpenExercise={onOpenExercise}
            />
        );
    }

    if (showVideoInline && exercise.VideoMetadata != null) {
        return (
            <VideoCardView
                key={exercise.Id}
                exercise={exercise}
                class={className ?? props.class}
                onVideoPlayed={onVideoPlayed}
            />
        );
    }

    if (exercise.Type === ExerciseType.Info) {
        return (
            <div className={classNames(className, props.class)} style={style}>
                <LabelHeader5 className="mb-10 mt-20">
                    {exercise.Name}
                </LabelHeader5>
                <LabelBody>{exercise.Description}</LabelBody>
            </div>
        );
    }

    const isDisabled = exercise.Status == ExerciseStatus.Blocked;
    const opacity = isDisabled ? 'opacity-60' : null;
    const headerTextColor =
        exercise.Type === ExerciseType.Goal
            ? 'text-dark-blue'
            : 'text-dark-red';
    const textColor =
        exercise.Type === ExerciseType.Goal
            ? 'text-dark-blue'
            : 'text-grey-900';

    return (
        <Card
            className={classNames(
                'flex flex-col text-left',
                className,
                props.class,
            )}
            style={style}
            disabled={isDisabled}
            type={
                exercise.Type === ExerciseType.WitnessStatement ||
                exercise.Type === ExerciseType.Feedback ||
                exercise.Type === ExerciseType.CheckIn
                    ? CardType.Grey
                    : exercise.Type === ExerciseType.Goal
                      ? CardType.Blue
                      : CardType.Red
            }
        >
            {exercise.ImageMetadata != null && (
                <img
                    className={classNames(opacity, 'mb-10 rounded-[10px]')}
                    src={exercise.ImageMetadata.Url}
                />
            )}
            {exercise.Type !== ExerciseType.Feedback &&
                exercise.Type !== ExerciseType.CheckIn && (
                    <div
                        className={classNames('flex', {
                            'mb-10':
                                exercise.Type === ExerciseType.WitnessStatement,
                            'mb-20':
                                exercise.Type !== ExerciseType.WitnessStatement,
                        })}
                    >
                        {exercise.CornerImageUrl || exercise.CornerImageText ? (
                            <>
                                {exercise.CornerImageUrl && (
                                    <img
                                        src={exercise.CornerImageUrl}
                                        alt=""
                                        className={classNames(
                                            'h-6 w-6 object-cover',
                                            opacity,
                                        )}
                                    />
                                )}

                                {exercise.CornerImageText && (
                                    <LabelBodySmall
                                        className={classNames(
                                            opacity,
                                            exercise.CornerImageUrl && 'ml-10',
                                        )}
                                    >
                                        {exercise.CornerImageText}
                                    </LabelBodySmall>
                                )}
                            </>
                        ) : exercise.Type === ExerciseType.Goal ? (
                            <>
                                <TargetCorrectIcon className={opacity} />
                                <LabelBodySmall
                                    className={classNames(opacity, 'ml-10')}
                                >
                                    Målsætning
                                </LabelBodySmall>
                            </>
                        ) : exercise.Type === ExerciseType.WitnessStatement ? (
                            <>
                                <MegaphoneIcon className={opacity} />
                                <LabelBodySmall
                                    className={classNames(opacity, 'ml-10')}
                                >
                                    Eksempel
                                </LabelBodySmall>
                            </>
                        ) : exercise.VideoMetadata != null ? (
                            <>
                                <VideoPlayerIcon className={opacity} />
                                <LabelBodySmall
                                    className={classNames(opacity, 'ml-10')}
                                >
                                    {Utils.formatTimespan(
                                        exercise.VideoMetadata.Duration,
                                    )}{' '}
                                    min
                                </LabelBodySmall>
                            </>
                        ) : exercise.Type === ExerciseType.Article ? (
                            <>
                                <ArticleIcon className={opacity} />
                                <LabelBodySmall
                                    className={classNames(opacity, 'ml-10')}
                                >
                                    Artikel
                                </LabelBodySmall>
                            </>
                        ) : (
                            <>
                                <NotesIcon className={opacity} />
                                <LabelBodySmall
                                    className={classNames(opacity, 'ml-10')}
                                >
                                    Øvelse
                                </LabelBodySmall>
                            </>
                        )}

                        <Spacer class="ml-20" />
                        {exercise.Type != ExerciseType.Article && (
                            <>
                                <ExerciseStatusView
                                    hasLoggedData={hasLoggedData}
                                    status={exercise.Status}
                                    isVideo={exercise.VideoMetadata != null}
                                />
                            </>
                        )}
                    </div>
                )}

            {exercise.VideoMetadata != null && (
                <img
                    className={classNames(opacity, 'mb-10 rounded-[10px]')}
                    src={exercise.VideoMetadata.PosterUrl}
                />
            )}
            <LabelDisplay1
                color={headerTextColor}
                className={classNames(opacity, 'mb-10')}
            >
                {exercise.Name}
            </LabelDisplay1>
            <LabelBody color={textColor} className={opacity}>
                {exercise.Description}
            </LabelBody>
            <Spacer />
            <div className="flex">
                <ExerciseActionButton
                    exercise={exercise}
                    onClick={onOpenExercise}
                    disabled={isDisabled}
                    className={classNames(opacity, 'mt-20')}
                    buttonType={buttonType}
                />
                <Spacer />
            </div>
            {children}
        </Card>
    );
}

type ExerciseActionButtonProps = {
    exercise: ExerciseDto;
    disabled?: boolean;
    className?: string;
    onClick: () => void;
    buttonType?: ButtonType;
};
function ExerciseActionButton({
    exercise,
    onClick,
    className,
    disabled,
    buttonType,
}: ExerciseActionButtonProps) {
    const label =
        exercise.ButtonText ||
        (exercise.Type === ExerciseType.WitnessStatement
            ? 'Læs Eksempel'
            : exercise.Type === ExerciseType.Feedback
              ? 'Gå til spørgeskema'
              : exercise.Type === ExerciseType.Goal
                ? 'Lav en målsætning'
                : exercise.VideoMetadata == null
                  ? 'Gå i gang'
                  : 'Se introduktion');

    if (disabled) {
        return <GhostButton className={className}>{label}</GhostButton>;
    }

    if (buttonType) {
        return (
            <Button
                variant={buttonType}
                onClick={onClick}
                className={className}
            >
                {label}
            </Button>
        );
    }

    switch (exercise.Type) {
        case ExerciseType.Goal:
            return (
                <TertiaryButton onClick={onClick} className={className}>
                    {label}
                </TertiaryButton>
            );
        case ExerciseType.Feedback:
        case ExerciseType.CheckIn:
            return (
                <GhostButton onClick={onClick} className={className}>
                    {label}
                </GhostButton>
            );
        default:
            return (
                <SecondaryButton onClick={onClick} className={className}>
                    {label}
                </SecondaryButton>
            );
    }
}
